/* ==========================================================================
   kit-interactions.js — the kit's ONE shared behaviour bundle
   --------------------------------------------------------------------------
   Four things live here, and they are here rather than in a section's own JS
   because in each case more than one section needs them and the failure mode of
   duplicating them is silent:

     1. KitInteractions.emitCategory / onCategory   (P26, the category bus)
        A named event instead of a cross-section DOM scan.
     2. KitInteractions.initTabs                    (the ARIA tab controller)
        There is no tab pattern anywhere in this theme. This is it.
     3. KitInteractions.lockScroll / unlockScroll   (P12, ref-counted)
        Four things want to lock body scroll; last one to close wins, and a
        naive implementation leaves the page permanently unscrollable.
     4. KitInteractions.revealNow                   (P3b)
        Reveals content that was display:none when the observer ran.

   THIS FILE'S API IS A CONTRACT. Sections 2b, 2d, 3a and 4a call into it and
   are read-only on it. Signatures are documented in docs/PRIMITIVES.md and must
   not change shape without updating every consumer.

   Loaded with defer from layout/theme.liquid, before the section stylesheets,
   so window.KitInteractions exists by DOMContentLoaded for any section script
   that runs after it. Everything is also safe to call before DOMContentLoaded —
   nothing here touches the DOM at definition time.
   ========================================================================== */

(function () {
  'use strict';

  var VERSION = 1;

  /* If the file somehow loads twice (a duplicated asset tag, an app injecting
     it), keep the first instance: the second would install a second set of
     listeners and every tab keypress would fire twice. */
  if (window.KitInteractions && window.KitInteractions.version >= VERSION) return;

  var CATEGORY_EVENT = 'kit:category-select';
  var TAB_EVENT = 'kit:tab-change';

  var SELECTOR = {
    tabs: '[data-kit-tabs]',
    tablist: '[data-kit-tablist]',
    tab: '[data-kit-tab]',
    panel: '[data-kit-tabpanel]',
  };

  var isDesignMode = !!(window.Shopify && window.Shopify.designMode);

  /* ------------------------------------------------------------------------
     P3b — revealNow(root)

     WHY THIS IS NECESSARY, not a nice-to-have. Alpha's reveal is an
     IntersectionObserver: an element only loses .scroll-trigger--offscreen when
     it intersects the viewport. An element inside a display:none container
     never intersects anything — it has no box — so the tiles in a tab panel
     that starts hidden are observed once, marked offscreen, and then stay
     invisible forever after the tab is selected. Every product-tabs panel past
     the first depends on this call.

     Delegates to animations.js's export when present, so the reveal logic has
     exactly one implementation. The local fallback exists for the case where
     `animations_reveal_on_scroll` is off and animations.js was never enqueued —
     the classes may still be in the markup, so they still need clearing.
     ------------------------------------------------------------------------ */
  function revealNow(root) {
    var scope = root || document;

    if (window.KitReveal && typeof window.KitReveal.revealNow === 'function') {
      return window.KitReveal.revealNow(scope);
    }

    var hidden = scope.querySelectorAll ? scope.querySelectorAll('.scroll-trigger--offscreen') : [];
    var count = 0;

    Array.prototype.forEach.call(hidden, function (el, index) {
      el.classList.remove('scroll-trigger--offscreen');
      if (el.hasAttribute('data-cascade')) {
        el.style.setProperty('--animation-order', index);
      }
      count++;
    });

    return count;
  }

  /* ------------------------------------------------------------------------
     P12 — ref-counted scroll lock

     The problem this solves: the nav overlay, the price-list panel, Dawn's
     HeaderDrawer and Dawn's ModalDialog all lock body scroll, and all four
     unlock it unconditionally on close. Open the overlay, open a modal from
     inside it, close the modal — and the page scrolls behind an overlay that is
     still up. Or the reverse, and the page is stuck.

     Ref counting by OWNER TOKEN rather than by a counter: a plain integer
     counter over-counts when a component locks twice (a re-render, a double
     event) and then under-counts on the single unlock, leaking the lock
     forever. A Set of owners is idempotent — locking twice with the same token
     is one lock, and unlocking an owner that never locked is a no-op.

     Dawn's own two lockers are NOT migrated onto this in this batch (they are
     live on every page). They use body classes .overflow-hidden-* which are
     independent of the html-level class here, so the two systems coexist: the
     page stays locked while either says so, and neither can unlock the other.
     ------------------------------------------------------------------------ */
  var scrollOwners = [];

  function ownerIndex(owner) {
    for (var i = 0; i < scrollOwners.length; i++) {
      if (scrollOwners[i] === owner) return i;
    }
    return -1;
  }

  function applyScrollLock() {
    var root = document.documentElement;

    if (scrollOwners.length > 0) {
      if (!root.classList.contains('kit-scroll-locked')) {
        /* Measure BEFORE hiding the scrollbar, then hold the width as padding
           so removing the scrollbar does not reflow the page a few pixels
           wider. Measured every time rather than cached: it changes with
           browser zoom and with an OS overlay-scrollbar preference. */
        var gutter = window.innerWidth - root.clientWidth;
        root.style.setProperty('--kit-scroll-lock-gutter', (gutter > 0 ? gutter : 0) + 'px');
        root.classList.add('kit-scroll-locked');
      }
    } else {
      root.classList.remove('kit-scroll-locked');
      root.style.removeProperty('--kit-scroll-lock-gutter');
    }

    return scrollOwners.length;
  }

  function lockScroll(owner) {
    var token = owner || 'anonymous';
    if (ownerIndex(token) === -1) scrollOwners.push(token);
    return applyScrollLock();
  }

  function unlockScroll(owner) {
    var token = owner || 'anonymous';
    var index = ownerIndex(token);
    if (index !== -1) scrollOwners.splice(index, 1);
    return applyScrollLock();
  }

  function isScrollLocked() {
    return scrollOwners.length > 0;
  }

  /* ------------------------------------------------------------------------
     P26 — the category bus

     Replaces the prototype's implicit contract, which was a document-level
     click listener doing closest('[data-cat]') on anything outside the product
     row. That works exactly until a second component grows a data-cat attribute
     for its own reasons, at which point clicking it silently switches a tab
     three sections down the page.

     A named event with a documented payload means an emitter (a plate in
     media-plate-grid, a footer link column) needs to know nothing about the
     consumer, and a consumer that is not on the page is not an error.

     ORDER MATTERS FOR EMITTERS. emitCategory is synchronous, so an anchor that
     both selects a tab and jumps to it must call this in its click handler and
     then let the default navigation happen — switch first, jump second.
     Jumping first scrolls to a panel that is still hidden.
     ------------------------------------------------------------------------ */
  function emitCategory(key, options) {
    if (!key) return false;
    var opts = options || {};

    document.dispatchEvent(
      new CustomEvent(CATEGORY_EVENT, {
        bubbles: false,
        cancelable: false,
        detail: {
          key: String(key),
          source: opts.source || null,
          scope: opts.scope || null,
        },
      })
    );

    return true;
  }

  function onCategory(handler) {
    if (typeof handler !== 'function') return function () {};

    var listener = function (event) {
      handler(event.detail || {}, event);
    };

    document.addEventListener(CATEGORY_EVENT, listener);

    /* Returns its own unsubscribe rather than expecting the caller to keep the
       wrapped listener — a section being re-rendered by the theme editor has to
       be able to tear its listener down, and it does not have the wrapper. */
    return function () {
      document.removeEventListener(CATEGORY_EVENT, listener);
    };
  }

  /* ------------------------------------------------------------------------
     The tab controller

     A full roving-tabindex ARIA tablist. Nothing in this theme implements the
     pattern — the only aria-selected / role=tab hits in the repo are the
     predictive-search combobox, which is a different pattern entirely.

     DOM CONTRACT (the section's Liquid must emit all of this):

       <div data-kit-tabs>                          <- controller root
         <div data-kit-tablist role="tablist">
           <button data-kit-tab="hair" role="tab"
                   id="tab-hair" aria-controls="panel-hair"
                   aria-selected="true" tabindex="0">Hair</button>
           ...
         </div>
         <div data-kit-tabpanel="hair" role="tabpanel"
              id="panel-hair" aria-labelledby="tab-hair" tabindex="0">...</div>
         ...
       </div>

     The controller owns aria-selected, tabindex, the `hidden` attribute on
     panels and the .is-active class on both. The Liquid should emit a sensible
     initial state anyway, so the component is correct before JS runs and with
     JS disabled — see the no-JS note in docs/PRIMITIVES.md.

     Keyboard, per the ARIA authoring practices: Arrow keys move with wraparound
     and ACTIVATE on move (follow-focus, correct for panels whose content is
     already loaded), plus Home and End. The prototype omitted arrows entirely,
     which made the tabs mouse-only.
     ------------------------------------------------------------------------ */

  /* Remembers the selected tab per section id so a theme-editor re-render
     restores it. Without this, every settings change a merchant makes while
     editing tab 3 snaps them back to tab 1. */
  var activeKeyMemory = {};

  var controllers = [];

  function findController(root) {
    for (var i = 0; i < controllers.length; i++) {
      if (controllers[i].root === root) return controllers[i];
    }
    return null;
  }

  /* Only direct descendants of THIS controller, so nested tab sets (a tabbed
     panel inside a tab) do not steal each other's tabs. */
  function ownElements(root, selector) {
    var all = root.querySelectorAll(selector);
    return Array.prototype.filter.call(all, function (el) {
      return el.closest(SELECTOR.tabs) === root;
    });
  }

  function sectionIdOf(root) {
    var section = root.closest('.shopify-section');
    if (section && section.id) return section.id;
    return root.id || null;
  }

  function initTabs(root, options) {
    if (!root) return null;

    var existing = findController(root);
    if (existing) return existing;

    var opts = options || {};
    var tablist = root.querySelector(SELECTOR.tablist);
    var tabs = ownElements(root, SELECTOR.tab);
    var panels = ownElements(root, SELECTOR.panel);

    /* A single tab is not a tablist; wiring one up would add keyboard
       affordances that go nowhere. */
    if (tabs.length < 2) return null;

    var vertical = root.hasAttribute('data-kit-tabs-vertical');
    var listensCategories = !root.hasAttribute('data-kit-tabs-ignore-categories');
    var sectionId = sectionIdOf(root);

    var controller = {
      root: root,
      tablist: tablist,
      tabs: tabs,
      panels: panels,
      keys: tabs.map(function (tab) {
        return tab.getAttribute('data-kit-tab');
      }),
      activeKey: null,
      activate: activate,
      next: function () {
        step(1, true);
      },
      previous: function () {
        step(-1, true);
      },
      destroy: destroy,
    };

    function panelFor(key) {
      for (var i = 0; i < panels.length; i++) {
        if (panels[i].getAttribute('data-kit-tabpanel') === key) return panels[i];
      }
      return null;
    }

    function indexOfKey(key) {
      return controller.keys.indexOf(key);
    }

    /* activate(key, { focus, emit })
         focus  — move DOM focus to the tab. True for keyboard navigation, false
                  for a programmatic switch (a deep link, the category bus), or
                  the page yanks focus away from wherever the user was.
         emit   — dispatch kit:tab-change. Suppressed when the switch was itself
                  caused by an incoming event, to avoid an event loop between
                  two components listening to each other. */
    function activate(key, activateOptions) {
      var settings = activateOptions || {};
      var index = indexOfKey(key);

      /* Fail silently on an unknown key. This is deliberate and is what lets a
         footer link emit a category the current page's tabs do not have. */
      if (index === -1) return false;
      if (controller.activeKey === key && settings.force !== true) {
        if (settings.focus) tabs[index].focus();
        return true;
      }

      tabs.forEach(function (tab, i) {
        var selected = i === index;
        tab.setAttribute('aria-selected', selected ? 'true' : 'false');
        tab.setAttribute('tabindex', selected ? '0' : '-1');
        tab.classList.toggle('is-active', selected);
      });

      panels.forEach(function (panel) {
        var selected = panel.getAttribute('data-kit-tabpanel') === key;
        panel.classList.toggle('is-active', selected);
        if (selected) {
          panel.removeAttribute('hidden');
        } else {
          panel.setAttribute('hidden', '');
        }
      });

      controller.activeKey = key;
      if (sectionId) activeKeyMemory[sectionId] = key;

      /* The panel has just gained a box for the first time, so anything inside
         it that was waiting on an IntersectionObserver has to be released now.
         This is the whole reason revealNow exists. */
      var panel = panelFor(key);
      if (panel) revealNow(panel);

      if (settings.focus) tabs[index].focus();

      if (settings.emit !== false) {
        root.dispatchEvent(
          new CustomEvent(TAB_EVENT, {
            bubbles: true,
            detail: { key: key, root: root, index: index },
          })
        );
      }

      return true;
    }

    function step(delta, focus) {
      var current = indexOfKey(controller.activeKey);
      if (current === -1) current = 0;
      /* Wraparound. The modulo dance handles the negative case, which a plain
         `% length` does not. */
      var next = (current + delta + controller.keys.length) % controller.keys.length;
      activate(controller.keys[next], { focus: focus });
    }

    function onClick(event) {
      var tab = event.target.closest(SELECTOR.tab);
      if (!tab || tabs.indexOf(tab) === -1) return;
      event.preventDefault();
      activate(tab.getAttribute('data-kit-tab'), { focus: true });
    }

    function onKeydown(event) {
      var tab = event.target.closest(SELECTOR.tab);
      if (!tab || tabs.indexOf(tab) === -1) return;

      var forward = vertical ? 'ArrowDown' : 'ArrowRight';
      var backward = vertical ? 'ArrowUp' : 'ArrowLeft';

      switch (event.key) {
        case forward:
          event.preventDefault();
          step(1, true);
          break;
        case backward:
          event.preventDefault();
          step(-1, true);
          break;
        case 'Home':
          event.preventDefault();
          activate(controller.keys[0], { focus: true });
          break;
        case 'End':
          event.preventDefault();
          activate(controller.keys[controller.keys.length - 1], { focus: true });
          break;
        default:
          break;
      }
    }

    var unsubscribeCategory = null;

    function bind() {
      if (tablist) {
        tablist.addEventListener('click', onClick);
        tablist.addEventListener('keydown', onKeydown);
      }
      if (listensCategories) {
        unsubscribeCategory = onCategory(function (detail) {
          /* A scoped emit addresses one tab set by root id; an unscoped emit
             addresses every tab set that happens to have the key. */
          if (detail.scope && detail.scope !== root.id) return;
          activate(detail.key, { focus: false, emit: false });
        });
      }
    }

    function destroy() {
      if (tablist) {
        tablist.removeEventListener('click', onClick);
        tablist.removeEventListener('keydown', onKeydown);
      }
      if (unsubscribeCategory) unsubscribeCategory();
      var i = controllers.indexOf(controller);
      if (i !== -1) controllers.splice(i, 1);
    }

    bind();
    controllers.push(controller);

    /* Initial state, in priority order: an explicit option, the remembered key
       for this section (theme-editor re-render), whatever the markup already
       marks selected, then the first tab. */
    var initial =
      opts.activeKey ||
      (sectionId && activeKeyMemory[sectionId]) ||
      (function () {
        for (var i = 0; i < tabs.length; i++) {
          if (tabs[i].getAttribute('aria-selected') === 'true') return controller.keys[i];
        }
        return null;
      })() ||
      controller.keys[0];

    activate(initial, { focus: false, emit: false, force: true });

    return controller;
  }

  function initAllTabs(root) {
    var scope = root || document;
    var roots = scope.querySelectorAll ? scope.querySelectorAll(SELECTOR.tabs) : [];
    var made = [];

    Array.prototype.forEach.call(roots, function (el) {
      var controller = initTabs(el);
      if (controller) made.push(controller);
    });

    /* A section can BE the tabs root rather than contain it. */
    if (scope.matches && scope.matches(SELECTOR.tabs)) {
      var self = initTabs(scope);
      if (self) made.push(self);
    }

    return made;
  }

  function getTabs(root) {
    if (!root) return null;
    var host = root.matches && root.matches(SELECTOR.tabs) ? root : root.closest(SELECTOR.tabs);
    return host ? findController(host) : null;
  }

  /* ------------------------------------------------------------------------
     Wiring
     ------------------------------------------------------------------------ */

  function boot() {
    initAllTabs(document);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  if (isDesignMode) {
    /* Re-render: the old controller's nodes are gone, so drop it and rebuild.
       activeKeyMemory carries the selected tab across. */
    document.addEventListener('shopify:section:load', function (event) {
      controllers
        .filter(function (c) {
          return !document.contains(c.root);
        })
        .forEach(function (c) {
          c.destroy();
        });
      initAllTabs(event.target);
    });

    document.addEventListener('shopify:section:unload', function (event) {
      controllers
        .filter(function (c) {
          return event.target.contains(c.root);
        })
        .forEach(function (c) {
          c.destroy();
        });
    });

    /* Selecting a block in the editor must open the tab that block lives in.
       This is not a nicety: panels are hidden by default, so a merchant editing
       a tile in tab 3 would otherwise be editing something they cannot see, and
       would reasonably conclude the setting is broken. */
    document.addEventListener('shopify:block:select', function (event) {
      var panel = event.target.closest(SELECTOR.panel);
      if (!panel) return;
      var controller = getTabs(panel);
      if (!controller) return;
      controller.activate(panel.getAttribute('data-kit-tabpanel'), { focus: false });
    });
  }

  window.KitInteractions = {
    version: VERSION,

    /* Event names, exposed so a consumer never has to retype the string. */
    CATEGORY_EVENT: CATEGORY_EVENT,
    TAB_EVENT: TAB_EVENT,

    /* P26 */
    emitCategory: emitCategory,
    onCategory: onCategory,

    /* P12 */
    lockScroll: lockScroll,
    unlockScroll: unlockScroll,
    isScrollLocked: isScrollLocked,

    /* P3b */
    revealNow: revealNow,

    /* Tabs */
    initTabs: initTabs,
    initAllTabs: initAllTabs,
    getTabs: getTabs,
  };
})();
